import { normalizeWidgetTheme, APIURL, cancelSelectEvent, getCSSStyles, getWidgetDataURI, apiGet, toggleWidgetTheme, isHeaderPinned, getHeaderPinStateClass, getHeaderPinButtonHTML, setupHeaderPin, getNoDataHTML, isEmptyWidgetData } from '../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from '../../types';

export class TraderverseCryptoBitcoinDominance {
  private container: HTMLElement;
  private shadowRoot: ShadowRoot;
  private config: WidgetConfig;
  private currentData: any = null;
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
  private widgetResizeObserver: ResizeObserver | null = null;
  private lastFetchedAt: Date | null = null;
  private updateInterval: number | null = null;

  constructor(config: WidgetConfig) {
    this.config = { ...this.getDefaultConfig(), ...config };
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    this.container = this.resolveContainer(config.container);
    this.shadowRoot = (this.container as any).shadowRoot || this.container.attachShadow({ mode: 'closed' });

    this.init();
    this.setupResizeObserver();
  }

  private setupResizeObserver(): void {
    this.widgetResizeObserver = new ResizeObserver(() => this.updateDisplay());
    setTimeout(() => {
      const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
      if (widget) this.widgetResizeObserver?.observe(widget);
    }, 0);
  }

  private getDefaultConfig(): Partial<WidgetConfig> {
    return {
      headerPinned: true,
      theme: 'default-dark',
      width: '100%',
      height: '400px',
      autosize: true
    };
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === 'string') {
      const el = document.getElementById(container) || document.querySelector(container);
      if (!el) throw new Error(`Container not found: ${container}`);
      return el as HTMLElement;
    }
    return container;
  }

  private async init(): Promise<void> {
    this.createWidget();
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    if (widget) widget.classList.add('loading');
    await this.attachStyles();
    await this.fetchAndRenderData();
    if (widget) widget.classList.remove('loading'); 

    this.emit('ready', {});
    this.config.onReady?.();
    cancelSelectEvent(this.shadowRoot);
  }

  private createWidget(): void {
    let themeClass = 'defaultDarkMode';
  let dataTheme = 'default-dark';
  
  if (this.config.theme === 'white') {
    themeClass = 'whiteMode';
    dataTheme = 'white';
  } else if (this.config.theme === 'black') {
    themeClass = 'blackMode';
    dataTheme = 'black';
  } else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}
    const widgetHtml = `
      <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
        <div class="headTop">
          <h4 class="title crypto">${this.getTitleIconSvg()} Bitcoin Dominance</h4>
            <div class="actionCol">${getHeaderPinButtonHTML(this.config)}<tooltip-wrapper id="lastUpdatedTooltip"><svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M0.625 6.625C0.625 7.41293 0.780195 8.19315 1.08172 8.9211C1.38325 9.64905 1.82521 10.3105 2.38236 10.8676C2.93951 11.4248 3.60095 11.8667 4.3289 12.1683C5.05685 12.4698 5.83707 12.625 6.625 12.625C7.41293 12.625 8.19315 12.4698 8.9211 12.1683C9.64905 11.8667 10.3105 11.4248 10.8676 10.8676C11.4248 10.3105 11.8667 9.64905 12.1683 8.9211C12.4698 8.19315 12.625 7.41293 12.625 6.625C12.625 5.0337 11.9929 3.50758 10.8676 2.38236C9.74242 1.25714 8.2163 0.625 6.625 0.625C5.0337 0.625 3.50758 1.25714 2.38236 2.38236C1.25714 3.50758 0.625 5.0337 0.625 6.625Z" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/> <path d="M6.625 3.2915V6.62484L8.625 8.62484" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/> </svg><tooltip><span>Last Updated</span><strong>N/A</strong></tooltip></tooltip-wrapper><i class="pi pi-ellipsis-v"></i></div>
        </div>

        <div class="inBody">
          <div class="optionsRow" data-testing-id="options-row">
            <div class="cryptoCardBoxes" id="btcValues" style="margin: 0 0 20px;" data-testing-id="btc-values-container"></div>
          </div>
          <div class="btcHistVal" data-testing-id="btc-historical-container">
            <div id="historicalCardsContainer" data-testing-id="historical-cards-container"></div>
            <div id="yearlyCardsContainer" data-testing-id="yearly-cards-container"></div>
          </div>
        </div>
      </div>
    `;

    this.shadowRoot.innerHTML = widgetHtml;
    setupHeaderPin(this.shadowRoot, this.config);

  }

  private async attachStyles(): Promise<void> {
    try {
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
    } catch (err) {
      console.error('Failed to load CSS:', err);
    }
  }

  private fmtPercent(value: number | null | undefined): string {
    if (value == null) return '--';
    const percent = Number(value);
    if (percent > 100) return '100.00%';
    if (percent < -100) return '-100.00%';
    return percent.toFixed(2) + '%';
  }

  private fmtDate(dateStr: string | null | undefined): string {
    if (!dateStr) return '--';
    const dt = new Date(dateStr);
    return `${dt.getFullYear()}-${(dt.getMonth() + 1).toString().padStart(2, '0')}-${dt.getDate().toString().padStart(2, '0')}`;
  }

  private getTokenIcon(type: 'btc' | 'eth' | 'oth'): string {
    if (type === 'btc') {
      return `<svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><g fill="none" fill-rule="evenodd"><circle cx="16" cy="16" r="16" fill="#F7931A"/><path fill="#FFF" fill-rule="nonzero" d="M23.189 14.02c.314-2.096-1.283-3.223-3.465-3.975l.708-2.84-1.728-.43-.69 2.765c-.454-.114-.92-.22-1.385-.326l.695-2.783L15.596 6l-.708 2.839c-.376-.086-.746-.17-1.104-.26l.002-.009-2.384-.595-.46 1.846s1.283.294 1.256.312c.7.175.826.638.805 1.006l-.806 3.235c.048.012.11.03.18.057l-.183-.045-1.13 4.532c-.086.212-.303.531-.793.41.018.025-1.256-.313-1.256-.313l-.858 1.978 2.25.561c.418.105.828.215 1.231.318l-.715 2.872 1.727.43.708-2.84c.472.127.93.245 1.378.357l-.706 2.828 1.728.43.715-2.866c2.948.558 5.164.333 6.097-2.333.752-2.146-.037-3.385-1.588-4.192 1.13-.26 1.98-1.003 2.207-2.538zm-3.95 5.538c-.533 2.147-4.148.986-5.32.695l.95-3.805c1.172.293 4.929.872 4.37 3.11zm.535-5.569c-.487 1.953-3.495.96-4.47.717l.86-3.45c.975.243 4.118.696 3.61 2.733z"/></g></svg>`;
    }
    if (type === 'eth') {
      return `<svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><g fill="none" fill-rule="evenodd"><circle cx="16" cy="16" r="16" fill="#627EEA"/><g fill="#FFF" fill-rule="nonzero"><path fill-opacity=".602" d="M16.498 4v8.87l7.497 3.35z"/><path d="M16.498 4L9 16.22l7.498-3.35z"/><path fill-opacity=".602" d="M16.498 21.968v6.027L24 17.616z"/><path d="M16.498 27.995v-6.028L9 17.616z"/><path fill-opacity=".2" d="M16.498 20.573l7.497-4.353-7.497-3.348z"/><path fill-opacity=".602" d="M9 16.22l7.498 4.353v-7.701z"/></g></g></svg>`;
    }
    return `<svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path fill="#ffffff" d="M16 13c-1.654 0-3 1.346-3 3s1.346 3 3 3 3-1.346 3-3-1.346-3-3-3zm-10 0c-1.654 0-3 1.346-3 3s1.346 3 3 3 3-1.346 3-3-1.346-3-3-3zm20 0c-1.654 0-3 1.346-3 3s1.346 3 3 3 3-1.346 3-3-1.346-3-3-3z"/></svg>`;
  }

  private tokenPill(type: 'btc' | 'eth' | 'oth', value: string, testingId: string, rawValue: string | number = ''): string {
    const cls = type === 'btc' ? 'btc neutral01' : type === 'eth' ? 'eth neutral02' : 'oth neutral03';
    const crypto = type === 'oth' ? 'other' : type;
    return `<div class="tokenDv ${cls}" data-testing-id="${testingId}" data-testing-crypto="${crypto}" data-value="${rawValue}"><span>${this.getTokenIcon(type)} ${value}</span></div>`;
  }

  private resolveDominancePoint(entry: any): any {
    if (!entry) return {};
    if (Array.isArray(entry.records) && entry.records.length) {
      // API records are newest-first
      return entry.records[0] ?? {};
    }
    // lastWeek / lastMonth nest dominance under `historical`
    if (entry.historical && typeof entry.historical === 'object') {
      return entry.historical;
    }
    return entry;
  }

  private getDominanceValues(point: any): { btc: number | null; eth: number | null; other: number | null } {
    // Prefer dominance % fields only — `btc`/`eth`/`other` on these payloads are market-cap USD
    const btc = point?.btc_dominance != null && !Number.isNaN(Number(point.btc_dominance))
      ? Number(point.btc_dominance)
      : null;
    const eth = point?.eth_dominance != null && !Number.isNaN(Number(point.eth_dominance))
      ? Number(point.eth_dominance)
      : null;
    const other = btc != null && eth != null ? 100 - btc - eth : null;
    return { btc, eth, other };
  }

  private ensureHistContainers(): void {
    const histVal = this.shadowRoot.querySelector('.btcHistVal') as HTMLElement | null;
    if (!histVal) return;
    if (histVal.querySelector('#historicalCardsContainer') && histVal.querySelector('#yearlyCardsContainer')) {
      return;
    }
    histVal.innerHTML = `
      <div id="historicalCardsContainer" data-testing-id="historical-cards-container"></div>
      <div id="yearlyCardsContainer" data-testing-id="yearly-cards-container"></div>
    `;
  }

  private async fetchAndRenderData(): Promise<void> {
    try {
      const res = await apiGet({
        method: 'GET',
        url: `${APIURL}/v1/crypto/crypto_market_cap_chart`,
        headers: { 'Content-Type': 'application/json' }
      });
      if (!res.ok) throw new Error('Failed to fetch chart data');
      this.currentData = await res.json();
      const fromApi = (this.currentData as any)?.last_updated_at ?? (this.currentData as any)?.data?.[0]?.last_updated_at;
      this.lastFetchedAt = fromApi ? new Date(fromApi) : new Date();
      this.updateDisplay();
      this.emit('dataUpdate', this.currentData);
    } catch (err) {
      console.error('Error fetching chart data:', err);
    }
  }

  private fmtUSD = (num: number | null) => {
    if (num == null || isNaN(num)) return "--";
    try {
      return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", notation: "compact", maximumFractionDigits: 2 }).format(num);
    } catch {
      return `$${(num / 1e12).toFixed(2)}T`;
    }
  };

  private trendClass(value: number | null | undefined): string {
    if (value == null) return '';
    return value >= 0 ? 'positive' : 'negative';
  }

  private renderHistoricalValues(): void {
    if (!this.currentData?.data?.length) return;

    this.ensureHistContainers();
    const container = this.shadowRoot.querySelector('#historicalCardsContainer');
    if (!container) return;

    const dataObj = this.currentData.data[0];
    // records are newest-first: [0]=today, [1]=yesterday
    const currentRecords = dataObj.current?.[0]?.records ?? [];
    const histYesterday = currentRecords.length > 1
      ? currentRecords[1]
      : (currentRecords[0] ?? {});
    const histLastWeek = this.resolveDominancePoint(dataObj.lastWeek?.[0]);
    const histLastMonth = this.resolveDominancePoint(dataObj.lastMonth?.[0]);

    let html = `
    <div class="cryptoCard" data-testing-id="historical-values-card">
      <h2 data-testing-id="historical-title">Historical Values</h2>
    `;

    const periods = [
      { label: 'Yesterday', data: histYesterday },
      { label: 'Last Week', data: histLastWeek },
      { label: 'Last Month', data: histLastMonth },
    ];

    for (let idx = 0; idx < periods.length; idx++) {
      const { label, data } = periods[idx];
      const { btc, eth, other } = this.getDominanceValues(data);

      html += `
        <div class="valueWrapper" data-testing-id="historical-period-${idx}" data-period="${label}">
          <p data-testing-id="period-name">${label}</p>
          <span class="expand"></span>
          ${this.tokenPill('btc', this.fmtPercent(btc), `period-btc-${idx}`, btc ?? '')}
          ${this.tokenPill('eth', this.fmtPercent(eth), `period-eth-${idx}`, eth ?? '')}
          ${this.tokenPill('oth', this.fmtPercent(other), `period-other-${idx}`, other ?? '')}
        </div>
      `;
    }

    html += `</div>`;
    container.innerHTML = html;
  }

  private renderYearlyHighLow(): void {
    if (!this.currentData?.data?.length) return;

    this.ensureHistContainers();
    const dataObj = this.currentData.data[0];
    const yHigh = this.resolveDominancePoint(dataObj.yearlyHighest?.[0] ?? dataObj.yearly?.high ?? dataObj.highest?.[0]);
    const yLow = this.resolveDominancePoint(dataObj.yearlyLowest?.[0] ?? dataObj.yearly?.low ?? dataObj.lowest?.[0]);
    const highVals = this.getDominanceValues(yHigh);
    const lowVals = this.getDominanceValues(yLow);

    const container = this.shadowRoot.querySelector('#yearlyCardsContainer');
    if (!container) return;

    const highDate = this.fmtDate(yHigh.timestamp || yHigh?.quote?.USD?.timestamp);
    const lowDate = this.fmtDate(yLow.timestamp || yLow?.quote?.USD?.timestamp);

    container.innerHTML = `
    <div class="cryptoCard" data-testing-id="yearly-high-low-card">
      <h2 data-testing-id="yearly-title">Yearly High and Low</h2>

      <div class="valueWrapper" data-testing-id="yearly-high-wrapper" data-type="high">
        <p data-testing-id="high-date">High • ${highDate}</p>
        <span class="expand"></span>
        ${this.tokenPill('btc', this.fmtPercent(highVals.btc), 'high-btc', highVals.btc ?? '')}
        ${this.tokenPill('eth', this.fmtPercent(highVals.eth), 'high-eth', highVals.eth ?? '')}
        ${this.tokenPill('oth', this.fmtPercent(highVals.other), 'high-other', highVals.other ?? '')}
      </div>

      <div class="valueWrapper" data-testing-id="yearly-low-wrapper" data-type="low">
        <p data-testing-id="low-date">Low • ${lowDate}</p>
        <span class="expand"></span>
        ${this.tokenPill('btc', this.fmtPercent(lowVals.btc), 'low-btc', lowVals.btc ?? '')}
        ${this.tokenPill('eth', this.fmtPercent(lowVals.eth), 'low-eth', lowVals.eth ?? '')}
        ${this.tokenPill('oth', this.fmtPercent(lowVals.other), 'low-other', lowVals.other ?? '')}
      </div>
    </div>
  `;
  }

  private renderBTCValues(): void {
    if (!this.currentData?.data?.length) return;

    const currentBlock = this.currentData.data[0]?.current?.[0];
    if (!currentBlock) return;

    // records are newest-first
    const latestRec = currentBlock.records?.[0];
    if (!latestRec) return;

    const { btc: btcDom, eth: ethDom, other: otherDom } = this.getDominanceValues(latestRec);

    const totalCap = latestRec?.quote?.USD?.total_market_cap ?? null;
    const btcCap = latestRec.btc ?? (totalCap != null && btcDom != null ? (totalCap * btcDom) / 100 : null);
    const ethCap = latestRec.eth ?? (totalCap != null && ethDom != null ? (totalCap * ethDom) / 100 : null);
    const otherCap = latestRec.other ?? (totalCap != null && otherDom != null ? (totalCap * otherDom) / 100 : null);

    const btcDomChange = currentBlock.btcDominanceChange ?? null;
    const ethDomChange = currentBlock.ethDominanceChange ?? null;
    const otherDomChange = currentBlock.otherDominanceChange ?? null;

    const container = this.shadowRoot.querySelector('#btcValues');
    if (!container) return;

    container.innerHTML = `
    <div class="cryptoCard" data-testing-id="btc-card" data-crypto="btc">
      <h2 data-testing-id="btc-title"><i class="dot" style="background-color:rgba(247,147,26,1);"></i> BTC</h2>
      <div class="valueWrapper" data-testing-id="btc-value-wrapper">
        <h3 data-testing-id="btc-market-cap">${this.fmtUSD(btcCap)}</h3>
        <span class="${this.trendClass(btcDomChange)}" data-testing-id="btc-change" data-change="${btcDomChange ?? 0}"> ${btcDomChange != null ? btcDomChange.toFixed(2) + '%' : '--'} </span>
      </div>
    </div>
    <div class="cryptoCard" data-testing-id="eth-card" data-crypto="eth">
      <h2 data-testing-id="eth-title"><i class="dot" style="background-color:rgba(98,126,234,1);"></i> ETH</h2>
      <div class="valueWrapper" data-testing-id="eth-value-wrapper">
        <h3 data-testing-id="eth-market-cap">${this.fmtUSD(ethCap)}</h3>
        <span class="${this.trendClass(ethDomChange)}" data-testing-id="eth-change" data-change="${ethDomChange ?? 0}"> ${ethDomChange != null ? ethDomChange.toFixed(2) + '%' : '--'} </span>
      </div>
    </div>
    <div class="cryptoCard" data-testing-id="other-card" data-crypto="other">
      <h2 data-testing-id="other-title"><i class="dot" style="background-color:rgba(153,155,159,1);"></i> Other</h2>
      <div class="valueWrapper" data-testing-id="other-value-wrapper">
        <h3 data-testing-id="other-market-cap">${this.fmtUSD(otherCap)}</h3>
        <span class="${this.trendClass(otherDomChange)}" data-testing-id="other-change" data-change="${otherDomChange ?? 0}"> ${otherDomChange != null ? otherDomChange.toFixed(2) + '%' : '--'} </span>
      </div>
    </div>
  `;
  }

  private formatLastUpdated(date: Date): string {
    const options: Intl.DateTimeFormatOptions = {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'America/New_York',
    };
    return date.toLocaleString('en-US', options).replace(',', '').replace(/\//g, '-') + ' EST';
  }

  private updateLastUpdatedTooltip(): void {
    const lastUpdated = this.lastFetchedAt ? this.formatLastUpdated(this.lastFetchedAt) : 'N/A';
    const tooltip = this.shadowRoot.querySelector('#lastUpdatedTooltip tooltip strong');
    if (tooltip) tooltip.innerHTML = lastUpdated.replace(' ', '<br>');
  }

  private updateDisplay(): void {
    this.updateLastUpdatedTooltip();
    const btcValues = this.shadowRoot.querySelector('#btcValues') as HTMLElement;
    if (isEmptyWidgetData(this.currentData?.data)) {
      if (btcValues) btcValues.innerHTML = getNoDataHTML();
      this.ensureHistContainers();
      const histContainer = this.shadowRoot.querySelector('#historicalCardsContainer');
      const yearlyContainer = this.shadowRoot.querySelector('#yearlyCardsContainer');
      if (histContainer) histContainer.innerHTML = '';
      if (yearlyContainer) yearlyContainer.innerHTML = '';
      return;
    }
    this.ensureHistContainers();
    this.renderBTCValues();
    this.renderHistoricalValues();
    this.renderYearlyHighLow();
  }

  public on(type: WidgetEventType, handler: WidgetEventHandler): void {
    if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
    this.eventHandlers.get(type)!.push(handler);
  }

  public off(type: WidgetEventType, handler: WidgetEventHandler): void {
    const list = this.eventHandlers.get(type);
    if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
  }
  
  public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
    if (!this.shadowRoot) return;
  
    this.config.theme = toggleWidgetTheme(
      this.shadowRoot,
      theme
    ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';
  
    this.updateDisplay();
  }

  public async getWidgetImage(): Promise<string> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    return getWidgetDataURI(widget);
  }

  public destroy(): void {
    if (this.updateInterval) clearInterval(this.updateInterval);
    if (this.widgetResizeObserver) this.widgetResizeObserver.disconnect();
    this.eventHandlers.clear();
    this.shadowRoot.innerHTML = '';
  }

  private emit(type: WidgetEventType, data: any): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers) handlers.forEach(h => h({ type, data }));
  }
  private getTitleIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M15.15 20.3C12.3125 20.3 10 17.9875 10 15.15C10 12.3125 12.3125 10 15.15 10C17.9875 10 20.3 12.3125 20.3 15.15C20.3 17.9875 18 20.3 15.15 20.3ZM15.15 11.8625C13.3375 11.8625 11.875 13.3375 11.875 15.1375C11.875 16.9375 13.35 18.4125 15.15 18.4125C16.95 18.4125 18.425 16.9375 18.425 15.1375C18.425 13.3375 16.9625 11.8625 15.15 11.8625Z" fill="currentColor"/> <path d="M15 28.4375C7.5875 28.4375 1.5625 22.4125 1.5625 15C1.5625 7.5875 7.5875 1.5625 15 1.5625C22.4125 1.5625 28.4375 7.5875 28.4375 15C28.4375 22.4125 22.4125 28.4375 15 28.4375ZM15 3.4375C8.625 3.4375 3.4375 8.625 3.4375 15C3.4375 21.375 8.625 26.5625 15 26.5625C21.375 26.5625 26.5625 21.375 26.5625 15C26.5625 8.625 21.375 3.4375 15 3.4375Z" fill="currentColor"/> <path d="M15.0625 22C14.5158 22 14.0625 21.5192 14.0625 20.9394V9.06061C14.0625 8.48081 14.5158 8 15.0625 8C15.6092 8 16.0625 8.48081 16.0625 9.06061V20.9394C16.0625 21.5192 15.6092 22 15.0625 22Z" fill="currentColor"/> </svg>`;
  }

}

declare global {
  interface Window { TraderverseCryptoBitcoinDominance: typeof TraderverseCryptoBitcoinDominance; }
}

if (typeof window !== 'undefined') {
  window.TraderverseCryptoBitcoinDominance = TraderverseCryptoBitcoinDominance;
}